import { ProcessStepForm } from '@/components/processes/ProcessStepForm';
import {
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyTableState,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDown,
  ChevronUp,
  ClipboardCopy,
  GripVertical,
  Import,
  ListChecks,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProcessStep {
  id: string;
  body: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface ProcessStepsListProps {
  /** Process ID used to build API endpoint paths */
  processId: string;
  /** Current list of steps, sorted by sortOrder */
  steps: ProcessStep[];
}

// ---------------------------------------------------------------------------
// Inline edit cell
// ---------------------------------------------------------------------------

interface InlineEditCellProps {
  value: string;
  onSave: (value: string) => Promise<void>;
  isPending: boolean;
}

/**
 * Renders step body text that becomes an editable input on click.
 * Saves on blur or Enter key; cancels on Escape.
 */
function InlineEditCell({ value, onSave, isPending }: InlineEditCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === value) {
      setEditing(false);
      setDraft(value);
      return;
    }
    await onSave(trimmed);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditing(false);
      setDraft(value);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={isPending}
        className="flex-1 min-w-0 text-sm border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary bg-background"
        aria-label="Edit step text"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => { setEditing(true); setDraft(value); }}
      title="Click to edit"
      className="flex-1 min-w-0 text-left text-sm text-foreground leading-relaxed group/edit flex items-center gap-1.5 cursor-text"
    >
      <span className="whitespace-pre-wrap break-words">{value}</span>
      <Pencil className="h-3 w-3 shrink-0 opacity-0 group-hover/edit:opacity-50 transition-opacity" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Sortable step row
// ---------------------------------------------------------------------------

interface SortableStepRowProps {
  step: ProcessStep;
  index: number;
  total: number;
  isUpdating: boolean;
  onBodySave: (step: ProcessStep, body: string) => Promise<void>;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  isReordering: boolean;
}

/**
 * A single sortable step row with drag handle, position number, inline edit,
 * up/down arrows, and delete.
 */
function SortableStepRow({
  step, index, total, isUpdating, onBodySave,
  onMoveUp, onMoveDown, onDelete, isReordering,
}: SortableStepRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-3 px-4 py-3 ${isDragging ? 'bg-muted shadow-lg rounded-lg' : ''}`}
    >
      {/* Drag handle */}
      <button
        type="button"
        className="touch-none shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-muted-foreground mt-0.5"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Position number */}
      <span className="flex-shrink-0 h-5 w-5 mt-0.5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">
        {index + 1}
      </span>

      {/* Editable body */}
      <InlineEditCell
        value={step.body}
        onSave={(body) => onBodySave(step, body)}
        isPending={isUpdating}
      />

      {/* Up / Down */}
      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          disabled={index === 0 || isReordering}
          onClick={onMoveUp}
          aria-label="Move step up"
        >
          <ChevronUp className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          disabled={index === total - 1 || isReordering}
          onClick={onMoveDown}
          aria-label="Move step down"
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
      </div>

      {/* Delete */}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-destructive hover:bg-destructive/10"
        onClick={onDelete}
        aria-label="Delete step"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Steps editor for a process.
 * Supports inline text editing, drag-and-drop reordering, up/down arrow
 * reordering, deletion, and adding new steps. Reordering and deletion are
 * optimistic — the UI updates instantly while the server syncs in the
 * background.
 *
 * @param processId - The process's ID used for API calls.
 * @param steps - Sorted list of current steps from the parent query.
 */
export function ProcessStepsList({ processId, steps }: ProcessStepsListProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importError, setImportError] = useState<string | null>(null);

  // Optimistic local order — used so reorder is instant
  const [optimisticSteps, setOptimisticSteps] = useState<ProcessStep[]>([]);

  useEffect(() => {
    setOptimisticSteps([...steps].sort((a, b) => a.sortOrder - b.sortOrder));
  }, [steps]);

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['Process', processId] }),
    [queryClient, processId],
  );

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // -------------------------------------------------------------------------
  // Create step
  // -------------------------------------------------------------------------
  const createMutation = useMutation({
    mutationFn: async (body: string) => {
      const nextOrder =
        optimisticSteps.length > 0
          ? optimisticSteps[optimisticSteps.length - 1].sortOrder + 10
          : 10;
      return apiClient.post(`/processes/${processId}/steps`, { body, sortOrder: nextOrder });
    },
    onSuccess: () => invalidate(),
    onError: (err: Error) => toast.error(err?.message ?? 'Failed to add step'),
  });

  // -------------------------------------------------------------------------
  // Update body
  // -------------------------------------------------------------------------
  const updateMutation = useMutation({
    mutationFn: async ({ stepId, payload }: { stepId: string; payload: Record<string, unknown> }) => {
      return apiClient.put(`/processes/${processId}/steps/${stepId}`, payload);
    },
    onSuccess: () => invalidate(),
    onError: (err: Error) => toast.error(err?.message ?? 'Failed to update step'),
  });

  // -------------------------------------------------------------------------
  // Delete step (optimistic)
  // -------------------------------------------------------------------------
  const deleteMutation = useMutation({
    mutationFn: async (stepId: string) => {
      return apiClient.delete(`/processes/${processId}/steps/${stepId}`);
    },
    onSuccess: () => invalidate(),
    onError: (err: Error) => {
      toast.error(err?.message ?? 'Failed to delete step');
      invalidate();
    },
  });

  // -------------------------------------------------------------------------
  // Reorder steps
  // -------------------------------------------------------------------------
  const reorderMutation = useMutation({
    mutationFn: async (reordered: Array<{ id: string; sortOrder: number }>) => {
      return apiClient.put(`/processes/${processId}/steps/reorder`, { steps: reordered });
    },
    onSuccess: () => invalidate(),
    onError: (err: Error) => {
      toast.error(err?.message ?? 'Failed to reorder steps');
      invalidate();
    },
  });

  /** Apply a new order optimistically and fire the server request. */
  const applyNewOrder = useCallback((newOrder: ProcessStep[]) => {
    const withNewOrders = newOrder.map((step, idx) => ({
      ...step,
      sortOrder: (idx + 1) * 10,
    }));
    setOptimisticSteps(withNewOrders);

    reorderMutation.mutate(
      withNewOrders.map((s) => ({ id: s.id, sortOrder: s.sortOrder })),
    );
  }, [reorderMutation]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = optimisticSteps.findIndex((s) => s.id === active.id);
    const newIndex = optimisticSteps.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...optimisticSteps];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    applyNewOrder(reordered);
  }, [optimisticSteps, applyNewOrder]);

  const moveStep = useCallback((stepId: string, direction: 'up' | 'down') => {
    const idx = optimisticSteps.findIndex((s) => s.id === stepId);
    if (idx === -1) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= optimisticSteps.length) return;

    const reordered = [...optimisticSteps];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(targetIdx, 0, moved);

    applyNewOrder(reordered);
  }, [optimisticSteps, applyNewOrder]);

  const handleAddStep = async (body: string) => {
    await createMutation.mutateAsync(body);
  };

  const handleBodySave = async (step: ProcessStep, body: string) => {
    await updateMutation.mutateAsync({ stepId: step.id, payload: { body } });
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setOptimisticSteps((prev) => prev.filter((s) => s.id !== deleteTarget));
    setDeleteTarget(null);
    deleteMutation.mutate(deleteTarget);
  };

  /** Copy all steps as a portable JSON array to clipboard. */
  const handleCopySteps = useCallback(() => {
    const exportData = optimisticSteps.map((s, idx) => ({ body: s.body, sortOrder: idx + 1 }));
    navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
    toast.success('Steps copied to clipboard');
  }, [optimisticSteps]);

  /** Import steps from a JSON array. */
  const handleImportSteps = useCallback(async () => {
    setImportError(null);
    try {
      const parsed = JSON.parse(importJson);
      if (!Array.isArray(parsed)) {
        setImportError('JSON must be an array of step objects');
        return;
      }

      const baseOrder =
        optimisticSteps.length > 0
          ? optimisticSteps[optimisticSteps.length - 1].sortOrder + 10
          : 10;

      for (let i = 0; i < parsed.length; i++) {
        const item = parsed[i];
        const body = typeof item === 'string' ? item : item?.body;
        if (!body || typeof body !== 'string') {
          setImportError(`Item ${i} is missing a "body" string`);
          return;
        }
        await apiClient.post(`/processes/${processId}/steps`, {
          body,
          sortOrder: item.sortOrder ?? baseOrder + i * 10,
        });
      }

      invalidate();
      setShowImportDialog(false);
      setImportJson('');
      toast.success(`Imported ${parsed.length} steps`);
    } catch (err) {
      setImportError(err instanceof SyntaxError ? 'Invalid JSON' : (err as Error).message);
    }
  }, [importJson, optimisticSteps, processId, apiClient, invalidate]);

  const deleteTargetStep = optimisticSteps.find((s) => s.id === deleteTarget);

  return (
    <div className="space-y-3">
      {optimisticSteps.length === 0 ? (
        <EmptyTableState
          icon={<ListChecks className="h-5 w-5" />}
          title="No steps yet"
          description="Add a step below to get started"
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={optimisticSteps.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="divide-y rounded-lg border bg-background">
              {optimisticSteps.map((step, idx) => (
                <SortableStepRow
                  key={step.id}
                  step={step}
                  index={idx}
                  total={optimisticSteps.length}
                  isUpdating={updateMutation.isPending}
                  onBodySave={handleBodySave}
                  onMoveUp={() => moveStep(step.id, 'up')}
                  onMoveDown={() => moveStep(step.id, 'down')}
                  onDelete={() => setDeleteTarget(step.id)}
                  isReordering={reorderMutation.isPending}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <ProcessStepForm
        onSubmit={handleAddStep}
        isPending={createMutation.isPending}
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopySteps}
              disabled={optimisticSteps.length === 0}
              title="Copy steps as JSON"
              type="button"
            >
              <ClipboardCopy className="h-3.5 w-3.5" />
              Copy Steps
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowImportDialog(true); setImportJson(''); setImportError(null); }}
              title="Import steps from JSON"
              type="button"
            >
              <Import className="h-3.5 w-3.5" />
              Import Steps
            </Button>
          </>
        }
      />

      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Steps</DialogTitle>
            <DialogDescription>
              Paste a JSON array of steps. Each item should have a &quot;body&quot; field.
              Imported steps are appended to the existing list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <textarea
              value={importJson}
              onChange={(e) => { setImportJson(e.target.value); setImportError(null); }}
              placeholder={'[\n  { "body": "First step", "sortOrder": 1 },\n  { "body": "Second step" }\n]'}
              className="w-full h-40 text-sm font-mono border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-background resize-none"
            />
            {importError && (
              <p className="text-sm text-destructive">{importError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleImportSteps} disabled={!importJson.trim()}>
              <Import className="h-4 w-4" />
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete Step"
        description={
          deleteTargetStep
            ? `Delete step: "${deleteTargetStep.body}"? This cannot be undone.`
            : 'Delete this step?'
        }
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
